<script setup>
import {register} from 'swiper/element/bundle';
import {nextTick, onMounted, ref, computed} from 'vue';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import OrderTermsModal from './modal/OrderTermsModal.vue';
import ReturnTermsModal from './modal/ReturnTermsModal.vue';
import { url } from '@/config/urls';
import router, { getPendingNav, clearPendingNav } from '@/router/index.js';
import { useAuthStore } from '@stores/auth.js';
import { apiError } from '@functions/apiError.js';
import { useScanNavigationStore } from '@stores/scanNavigation.js';

const emit = defineEmits(['close']);

const supportLinkList = ref([]);
const errorMessages = ref([]);
// 権限状態
const auth = useAuthStore();
const scanNav = useScanNavigationStore();
const isAuthenticated = computed(() => auth.isAuthenticated);

const user = computed(() => auth.user || {
  orderFlg: null,
  isPermittedOrder: false,
  isOrderTermsAgreed: false,
  isPermittedReturnRequest: false,
  isReturnTermsAgreed: false,
  formalName1: '',
  formalName2: '',
  customerUnityCode: '',
  email: ''
})

// 連打防止
const isDisabledLogoutButton = ref(false);

// ローディングフラグ
const isLoading = ref(false);

// ネイティブアプリのバージョン名（App Store / Play の versionName）
const appVersion = ref('');

const loadAppVersion = async () => {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { version } = await App.getInfo();
    appVersion.value = version || '';
  } catch {
    // 取得失敗時は非表示のまま
  }
};

// 閉じるボタン押下時
const onClickCloseButton = () => {
  emit('close');
}

// ユーザー管理TOP押下時
const onClickSettingTop = () => {
  onClickCloseButton();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// ログアウト処理
const logout = async() => {
  if (isDisabledLogoutButton.value) return;
  isDisabledLogoutButton.value = true;

  try {
    // メニューを閉じる
    emit('close');
    await auth.logout();
  } catch(e) {
    router.replace('/error');
  } finally {
    isDisabledLogoutButton.value = false;
  }
};

// 発注ルール同意モーダル「同意する」ボタン押下時
const agreeOrderTerms = async() => {
  isLoading.value = true;
  try {
    // 発注ルール同意API呼び出し
    await axios.post(props.orderTermsAgreeApiUrl);

    // authストアからユーザー情報を再取得
    await auth.fetchUserData();

    const pending = getPendingNav();
    if (pending?.path === '/order-scan') {
      clearPendingNav();
      scanNav.requestOrderScanResume();
    } else if (pending) {
      clearPendingNav();
      router.push(pending);
    } else {
      router.push(props.orderSearchPageUrl);
    }
  } catch(error) {
    if (error?.response?.status === 401) {
      router.push(props.loginUrl);
    } else {
      router.push(props.error);
    }
  } finally {
    isLoading.value = false;
  }
}

// 返品ルール同意モーダル「同意する」ボタン押下時
const agreeReturnTerms = async() => {
  isLoading.value = true;
  try {
    // 返品ルール同意API呼び出し
    await axios.post(props.returnTermsAgreeApiUrl);

    // authストアからユーザー情報を再取得
    await auth.fetchUserData();

    const pending = getPendingNav();
    if (pending?.path === '/return-scan') {
      clearPendingNav();
      scanNav.requestReturnScanResume();
    } else if (pending) {
      clearPendingNav();
      router.push(pending);
    } else {
      router.push(props.returnSearchPageUrl);
    }
  } catch(error) {
    if(error?.response?.status === 401) {
      router.push(props.loginUrl);
    } else {
      router.push(props.error);
    }
  } finally {
    isLoading.value = false;
  }
}

const handleOrderTermsPdf = () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') {
    router.push('/pdf-viewer/order-terms');
  } else {
    window.open('/front/pdf-viewer/order-terms', '_blank', 'noopener');
  }
  onClickCloseButton();
}

const handleReturnTermsPdf = () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') {
    router.push('/pdf-viewer/return-terms');
  } else {
    window.open('/front/pdf-viewer/return-terms', '_blank', 'noopener');
  }
  onClickCloseButton();
}

// コンポーネントマウント後の処理
onMounted(() => {
  loadAppVersion();

  // 顧客支援リンク一覧取得APIの呼び出し
  axios.get(props.supportLinkListUrl, {params: {size: 100}})
  .then(response => {
    const supportLinks = response.data.response.supportLinks;
    if (supportLinks && supportLinks.length > 0) {
      supportLinkList.value = supportLinks;

      // スライダーの描画
      nextTick(() => {
        register();
        const swiperEl = document.querySelector('#side-swiper');
        if(!swiperEl) return;
        Object.assign(swiperEl, {
          loop: true,
          autoplay: {
            delay: 5000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          },
          speed: 500,
          navigation: {
            nextEl: ".com_carousel2 .swiper-button-next",
            prevEl: ".com_carousel2 .swiper-button-prev",
          },
          slidesPerView: 1
        });
        swiperEl.initialize();
      });
    }
  })
  .catch(error => {
    if (error?.response?.status === 401) {
      router.replace(props.loginUrl);
    } else {
      apiError(error, errorMessages);
    }
  })
});

// コンポーネントに渡されたpropsの取得
const props = defineProps({
  supportLinkListUrl: {type: String, default: url.api.supportLink},
  logoutUrl: {type: String, default: url.logout},
  topPageUrl: {type: String, default: url.page.top},
  settingPageUrl: {type: String, default: url.page.setting.index},
  orderSearchPageUrl: {type: String, default: url.page.orderSearch},
  orderHistoryPageUrl: {type: String, default: url.page.orderHistory},
  orderFavoritePageUrl: {type: String, default: url.page.orderFavorite},
  orderCartPageUrl: {type: String, default: url.page.orderCart},
  orderResultsPageUrl: {type: String, default: url.page.orderResults},
  userDataUrl: {type: String, default: url.api.userData},
  orderTermsApiUrl: {type: String, default: url.api.orderTerms.pdf},
  orderTermsAgreeApiUrl: {type: String, default: url.api.orderTerms.agree},
  returnSearchPageUrl: {type: String, default: url.page.returnSearch},
  returnHistoryPageUrl: {type: String, default: url.page.returnHistory},
  returnBoxPageUrl: {type: String, default: url.page.returnBox},
  returnTermsAgreeApiUrl: {type: String, default: url.api.returnTerms.agree},
  returnTermsApiUrl: {type: String, default: url.api.returnTerms.pdf},
  loadingIcon: {type: String, default: url.static.loadingIcon},
  loginUrl: {type: String, default: url.login},
  error: {type: String, default: url.error},
  aboutPageUrl: {type: String, default: url.page.other.about},
  questionsPageUrl: {type: String, default: url.page.other.questions},
  termsPageUrl: {type: String, default: url.page.other.terms},
  environmentPageUrl: {type: String, default: url.page.other.environment},
  companyPageUrl: {type: String, default: url.page.other.company},
});
</script>

<template>
  <nav id="com_app_menu1">
    <div class="menu_bg1" @click="onClickCloseButton"></div>
    <div class="menu_wrap1">
      <div class="data1">
        <p class="text1">
          <span v-if="user.formalName1 || user.formalName2">
            {{ user.formalName2 + ' ' + user.formalName1 + ' ' }}
          </span>
          様
        </p>
        <p class="text2">
          <template v-if="user.customerUnityCode">ご利用ID: {{ user.customerUnityCode }}　</template>
          <br class="dsp-s" v-if="user.customerUnityCode && user.email">
          <template v-if="user.email">ご登録アドレス: {{ user.email }}</template>
        </p>
      </div>
      <ul class="menu1">
        <li><router-link to="/" @click="onClickCloseButton"><img src="/front/assets/common/img/icon_home1_on.png" alt="">ポータルTOP</router-link></li>
      </ul>
      <h3 class="title1">業務メニュー</h3>
      <ul class="menu2">
        <li>
          <router-link :to="props.orderResultsPageUrl" class="btn1" @click="onClickCloseButton">
            <img src="/front/assets/common/img/icon_order_result1_on.png" alt="">
            <span>注文結果</span>
          </router-link>
        </li>
        <li v-if="user.orderFlg === 0">
          <div class="btn1">
            <img src="/front/assets/common/img/icon_order1_on.png" alt="">
            発注
          </div>
          <ul class="sub1">
            <li><router-link :to="props.orderSearchPageUrl" @click="onClickCloseButton">発注TOP</router-link></li>
            <li><router-link :to="props.orderFavoritePageUrl" @click="onClickCloseButton">お気に入り</router-link></li>
            <li><router-link :to="props.orderCartPageUrl" @click="onClickCloseButton">カート</router-link></li>
            <li><router-link :to="props.orderHistoryPageUrl" @click="onClickCloseButton">発注履歴</router-link></li>
          </ul>
        </li>
        <li v-else-if="user.orderFlg === 1 || user.orderFlg === 2"><router-link :to="props.orderSearchPageUrl" class="btn1" @click="onClickCloseButton"><img src="/front/assets/common/img/icon_stock_inquiry1_on.png" alt="">在庫問合せ</router-link></li>
        <li v-if="user.isPermittedReturnRequest"><div class="btn1"><img src="/front/assets/common/img/icon_return_request1_on.png" alt="">返品依頼</div>
          <ul class="sub1">
            <li><router-link :to="props.returnSearchPageUrl" @click="onClickCloseButton">返品依頼TOP</router-link></li>
            <li><router-link :to="props.returnBoxPageUrl" @click="onClickCloseButton">返品ボックス</router-link></li>
            <li><router-link :to="props.returnHistoryPageUrl" @click="onClickCloseButton">返品履歴</router-link></li>
          </ul>
        </li>
        <li><div class="btn1"><img src="/front/assets/common/img/icon_user1_on.png" alt="">ユーザー管理</div>
          <ul class="sub1">
            <li><router-link :to="props.settingPageUrl" @click="onClickSettingTop">ユーザー管理TOP</router-link></li>
            <li><router-link :to="{ path: props.settingPageUrl, hash: '#personal-setting'}" @click="onClickCloseButton">個人設定</router-link></li>
            <li><router-link :to="{ path: props.settingPageUrl, hash: '#password-setting'}" @click="onClickCloseButton">パスワード設定</router-link></li>
            <li v-if="user.isPermittedOrder"><router-link :to="props.settingPageUrl + '#service-apply'" @click="onClickCloseButton">機能追加の申し込み</router-link></li>
            <li v-if="user.isPermittedReturnRequest"><a href="#" @click.prevent="handleReturnTermsPdf" target="_blank" rel="noopener">返品ルール</a></li>
            <li v-if="user.isPermittedOrder"><a href="#" @click.prevent="handleOrderTermsPdf" target="_blank" rel="noopener">発注ルール</a></li>
          </ul>
        </li>
      </ul>
      <a href="javascript:void(0)" :class="['button1' , { 'is-disabled': isDisabledLogoutButton }]" @click.prevent="logout">ログアウト</a>
      <h3 class="title1">各種リンク</h3>
      <ul class="com_bn_links1" v-if="supportLinkList.length > 0">
        <li v-for="supportLink in supportLinkList" :key="supportLink.id">
          <a :href="supportLink.url" target="_blank">
            <img :src="supportLink.imagePath" :alt="supportLink.title || ''">
          </a>
        </li>
      </ul>
      <h3 class="title1">その他</h3>
      <ul class="menu3">
        <li><a :href="props.companyPageUrl" target="_blank">会社案内</a></li>
        <li><a :href="props.aboutPageUrl" target="_blank">
            マニュアル
            <img src="/front/assets/common/img/icon_pdf2.png" alt="PDF" class="com_icon_pdf1">
          </a>
        </li>
        <li><a :href="props.environmentPageUrl" target="_blank">
            サイトの利用環境
            <img src="/front/assets/common/img/icon_pdf2.png" alt="PDF" class="com_icon_pdf1">
          </a>
        </li>
        <li><a :href="props.termsPageUrl" target="_blank">
            利用規約
            <img src="/front/assets/common/img/icon_pdf2.png" alt="PDF" class="com_icon_pdf1">
          </a>
        </li>
        <li><a :href="props.questionsPageUrl" target="_blank">困ったときのQ&A</a></li>
      </ul>
      <p v-if="appVersion" class="app_version1">Ver. {{ appVersion }}</p>
      <div id="com_app_menu_button_close1" @click="onClickCloseButton">
        <div><span></span><span></span><span></span></div>
        <span class="txt1"></span>
      </div>
    </div>
  </nav>

  <teleport to="body">
    <OrderTermsModal v-if="isAuthenticated"
      @agreeOrderTerms="agreeOrderTerms"
    ></OrderTermsModal>
    
    <ReturnTermsModal v-if="isAuthenticated"
      @agreeReturnTerms="agreeReturnTerms"
    ></ReturnTermsModal>
  </teleport>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>